import { z } from 'zod';
import { ENVIRONMENT_TYPES, LICENCE_KINDS, LICENCE_STATUSES } from '../enums.js';

export const LICENCE_FILE_TYP = 'siteflow-licence+json';
export const LICENCE_ASSERTION_TYP = 'siteflow-assert+json';

export const SignedHeaderSchema = z.object({
  alg: z.literal('EdDSA'),
  kid: z.string().min(1).max(64),
  typ: z.enum([LICENCE_FILE_TYP, LICENCE_ASSERTION_TYP]),
  v: z.literal(1),
});
export type SignedHeader = z.infer<typeof SignedHeaderSchema>;

export const FingerprintRequirementSchema = z.object({
  required: z.boolean(),
  hashes: z.array(z.string().length(64)).max(50).default([]),
});

/** Offline licence file payload (air-gapped installs). */
export const LicenceFilePayloadSchema = z.object({
  fileId: z.uuid(),
  licenceId: z.uuid(),
  organisationId: z.uuid(),
  organisationName: z.string().min(1).max(200),
  product: z.string().min(1).max(100),
  plan: z.string().min(1).max(100),
  kind: z.enum(LICENCE_KINDS),
  status: z.enum(LICENCE_STATUSES),
  issuedAt: z.iso.datetime({ offset: true }),
  expiresAt: z.iso.datetime({ offset: true }).nullable(),
  graceHours: z.number().int().nonnegative(),
  maxProductionSites: z.number().int().nonnegative().nullable(),
  addonProductionSites: z.number().int().nonnegative(),
  allowedDomains: z.array(z.string().max(255)).default([]),
  allowedIps: z.array(z.string().max(64)).default([]),
  allowedEnvironments: z.array(z.enum(ENVIRONMENT_TYPES)).default([...ENVIRONMENT_TYPES]),
  fingerprint: FingerprintRequirementSchema,
  features: z.record(z.string(), z.union([z.boolean(), z.record(z.string(), z.unknown())])).default({}),
  supersedes: z.uuid().nullable().default(null),
});
export type LicenceFilePayload = z.infer<typeof LicenceFilePayloadSchema>;

export const LicenceFileSchema = z.object({
  header: SignedHeaderSchema,
  payload: LicenceFilePayloadSchema,
  signature: z.string().min(1),
});
export type LicenceFile = z.infer<typeof LicenceFileSchema>;

/** Signed response of POST /licences/validate. */
export const ValidationAssertionSchema = z.object({
  iss: z.literal('siteflow-licence'),
  sub: z.uuid(),
  aud: z.uuid().nullable(),
  jti: z.uuid(),
  iat: z.number().int(),
  exp: z.number().int(),
  grace: z.number().int(),
  srv: z.number().int(),
  status: z.enum(LICENCE_STATUSES),
  valid: z.boolean(),
  reason: z.string().max(120).nullable().default(null),
  kind: z.enum(LICENCE_KINDS),
  plan: z.object({ key: z.string(), name: z.string() }),
  limits: z.object({
    productionSites: z.number().int().nonnegative().nullable(),
    usedSlots: z.number().int().nonnegative(),
  }),
  features: z.record(z.string(), z.union([z.boolean(), z.record(z.string(), z.unknown())])).default({}),
  domains: z.array(z.string()).default([]),
  environments: z.array(z.enum(ENVIRONMENT_TYPES)).default([]),
  activation: z
    .object({
      id: z.uuid(),
      siteEnvironmentId: z.uuid().nullable(),
      environmentType: z.enum(ENVIRONMENT_TYPES),
      domain: z.string().nullable(),
      instanceId: z.string().nullable(),
    })
    .nullable(),
  nonce: z.string().max(128).nullable().default(null),
});
export type ValidationAssertion = z.infer<typeof ValidationAssertionSchema>;

/** Wire format for licence key activation / validation requests. */
export const LICENCE_KEY_PATTERN = /^SFLOW(-[A-Z0-9]{4}){4}$/;

export const SignedRequestMetaSchema = z.object({
  nonce: z.string().min(16).max(128),
  timestamp: z.number().int().positive(),
});
